import { Row, Col, message, Table, Pagination, Tag, Input } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { GrView } from "react-icons/gr";
import { IVendorB2c } from "common/interface/IVendorItems";
import { getVendorB2cAll } from "store/vendor-items/vendorItemsSlice";
import { routes } from "common/routes";

const MESSAGE_KEY = "vendor_items";

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const StyledActionLink = styled(NavLink)`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VendorItemsPage = () => {
  useDocumentTitle("Vendor Items");
  const dispatch = useAppDispatch();
  const { vendors, status, error } = useAppSelector((state) => state.vendorItems);
  const [searchQuery, setSearchQuery] = useState("");

  const getData = useCallback(
    (pageNum = 1, pageSize = 25) => {
      dispatch(getVendorB2cAll({ pageNo: pageNum, pageSize }));
    },
    [dispatch]
  );

  useEffect(() => {
    getData(1, 25);
  }, [getData]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading Vendors", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [status, error]);

  const columns: ColumnsType<IVendorB2c> = [
    {
      key: "vendorCode",
      title: "Vendor Code",
      dataIndex: "vendorCode",
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
    },
    {
      key: "pocName",
      title: "POC Name",
      dataIndex: "pocName",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    {
      key: "primaryPhone",
      title: "Phone",
      dataIndex: "primaryPhone",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (val: string | null) =>
        val === "ACTIVE" ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">{val ?? "-"}</Tag>
        ),
    },
    {
      key: "actions",
      title: "Actions",
      render: (_, record) => (
        <StyledActionLink
          to={`/${routes.PO.VENDOR_ITEMS_MAPPING.replace(":vendorId", String(record.id))}`}
          state={record}
        >
          <GrView />
        </StyledActionLink>
      ),
    },
  ];

  const allRows = vendors?.data ?? [];
  const query = searchQuery.trim().toLowerCase();
  const rows = query
    ? allRows.filter(
        (v) =>
          v.name?.toLowerCase().includes(query) ||
          v.vendorCode?.toLowerCase().includes(query) ||
          v.pocName?.toLowerCase().includes(query)
      )
    : allRows;

  const Paginator = () => {
    if (!vendors || vendors.total <= vendors.pageSize || query) return <></>;
    const { pageNo, pageSize, total } = vendors;
    return (
      <StyledPagination
        current={pageNo}
        pageSize={pageSize}
        total={total}
        onChange={(page, size) => getData(page, size)}
      />
    );
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Vendor Items" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <Input.Search
              placeholder="Search by name, vendor code or POC..."
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 300 }}
            />
          </div>
        </AppActionStrip>
      </Row>
      {rows.length > 0 ? (
        <Row>
          <Col xs={24}>
            <Table<IVendorB2c>
              columns={columns}
              dataSource={rows}
              rowKey="id"
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={status === "loading"}
              bordered
            />
          </Col>
          <Col xs={24}>
            <Paginator />
          </Col>
        </Row>
      ) : (
        status !== "loading" && <>No Vendors to show.</>
      )}
    </>
  );
};

export default VendorItemsPage;
